import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateAgendaDtoSchema,
  PatchAgendaDtoSchema,
  type Agenda,
} from "@repo/types";
import {
  cacheDefaults,
  hookPriorities,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { nullObservable } from "@repo/shared-client/commonObservables";
import { Observable, of } from "rxjs";
import type { OnlyRequire } from "@repo/lib/types";
import { agendaModuleRepo, type AgendaModuleDto } from "./agendaModuleRepo";
import { letterHeadRepo, type LetterHeadDto } from "./letterHeadRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type AgendaGetKey = { type: "get"; tenantId: string; agendaId: string };
type AgendaListKey = { type: "list"; tenantId: string };
type AgendaKey = AgendaGetKey | AgendaListKey;

export type AgendaDto = Agenda & {
  tenantId: string;
  agendaModules: AgendaModuleDto[];
  letterHead: LetterHeadDto | null;
};

export type AgendaIdPath = { tenantId: string; agendaId: string | null };

export class AgendaRepository {
  private cache: RepoCache<string, AgendaDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<AgendaIdPath, AgendaDto>();
  private subscribeMap = new SubscriptionMap();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(agenda: AgendaDto) {
    this.cache.publish(agenda.id, agenda);
  }

  agendaToDto(tenantId: string, agenda: Agenda): AgendaDto;
  agendaToDto(tenantId: string, agenda?: null): null;
  agendaToDto(tenantId: string, agenda?: Agenda | null): AgendaDto | null;
  agendaToDto(tenantId: string, agenda?: Agenda | null): AgendaDto | null {
    const agendaModules =
      agenda?.agendaModules?.map((agendaModule) =>
        agendaModuleRepo.agendaModuleToDto(tenantId, agendaModule)
      ) ?? [];
    const letterHead = letterHeadRepo.letterHeadToDto(
      tenantId,
      agenda?.letterHead
    );
    return agenda ? { ...agenda, tenantId, agendaModules, letterHead } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as AgendaKey;

        switch (key.type) {
          case "get": {
            const { tenantId, agendaId } = key;
            const agenda = (await apiService.v1.getAgenda(tenantId, agendaId))
              .data;
            publish(agendaId, this.agendaToDto(tenantId, agenda));
            break;
          }
          case "list": {
            const { tenantId } = key;
            const agendas = (await apiService.v1.listAgendas(tenantId)).data;
            for (const agenda of agendas) {
              publish(agenda.id, this.agendaToDto(tenantId, agenda));
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as AgendaKey;

        switch (key.type) {
          case "get": {
            return table.get(key.agendaId) ?? null;
          }
          case "list": {
            const agendas: AgendaDto[] = [];
            for (const agenda of table.values()) {
              if (agenda.tenantId === key.tenantId) {
                agendas.push(agenda);
              }
            }
            return agendas;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as AgendaKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.agendas",
          key.type === "get" ? [key.agendaId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      subscriptionPublished: ({ strKey, requestKey, collectedData }) => {
        const key = requestKey as AgendaKey;

        switch (key.type) {
          case "get": {
            const data = collectedData as AgendaDto | null;
            if (!data) {
              this.subscribeMap.unsubscribe(strKey);
              break;
            }

            this.subscribeMap.updateChildren({
              strKey,
              idArray: (data.agendaModuleIds ??= []),
              objectArray: (data.agendaModules ??= []),
              subscribeChild: (id) =>
                agendaModuleRepo.get(key.tenantId, id, { initialFetch: false }),
              publishParent: (idArray, objectArray) => {
                data.agendaModuleIds = idArray;
                data.agendaModules = objectArray;
                this.cache.publish(key.agendaId, data);
              },
            });
            break;
          }
        }
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, agendaId: string): AgendaGetKey {
    return { type: "get", tenantId, agendaId };
  }
  private listKey(tenantId: string): AgendaListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    agendaId: string,
    options?: GetObservableOptions
  ): Observable<AgendaDto | null> {
    if (!tenantId || !agendaId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, agendaId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, agendaId: string): AgendaDto | null {
    const key = this.getKey(tenantId, agendaId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<AgendaDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): AgendaDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Omit<Partial<AgendaDto>, "agendaModules" | "letterHead">
  ): Promise<AgendaDto> {
    if (!tenantId) {
      throw new Error("Failed to create agenda: tenantId is blank");
    }

    const hooks = this.hooks.execute({ tenantId, agendaId: null }, "create");
    data = await hooks.before(data);

    let created = this.agendaToDto(
      tenantId,
      (
        await apiService.v1.createAgenda(
          tenantId,
          CreateAgendaDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new agenda
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    agendaId: string,
    data: Omit<Partial<AgendaDto>, "agendaModules" | "letterHead">
  ): Promise<AgendaDto> {
    if (!tenantId || !agendaId) {
      throw new Error("Failed to update agenda: tenantId or agendaId is blank");
    }

    const hooks = this.hooks.execute({ tenantId, agendaId }, "update");
    data = await hooks.before(data);

    let updated = this.agendaToDto(
      tenantId,
      (
        await apiService.v1.updateAgenda(
          tenantId,
          agendaId,
          PatchAgendaDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated agenda
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, agendaId: string): Promise<void> {
    if (!tenantId || !agendaId) {
      throw new Error("Failed to delete agenda: tenantId or agendaId is blank");
    }

    const hooks = this.hooks.execute({ tenantId, agendaId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteAgenda(tenantId, agendaId);

    await hooks.after(null);
    this.cache.publish(agendaId, null);
  }

  invalidate(agenda: OnlyRequire<AgendaDto, "id">) {
    this.cache.invalidateEntry(agenda.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<AgendaDto>): AgendaDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      name: "",
      description: "",
      agendaModuleIds: [],
      agendaModules: [],
      letterHead: null,
      ...data,
    });
  }
}

export const agendaRepo = new AgendaRepository();

agendaRepo.addHook(() => {
  const before = (agenda: Partial<AgendaDto>) => {
    return { ...agenda, agendaModules: undefined, letterHead: undefined };
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.removeNestedEntities);
